import Vue from "vue";

import axios from "axios";
import {
  SESSION_EXPIRED_HANDLED,
  getSessionHeaderId,
  handleSessionExpiredResponse,
  isPublicAuthUrl,
  isSessionExpiredError,
  isUsageTrackingUrl,
} from "./sessionService";
window.axios = axios;
// Q-17 (AB#3390 W7): send httpOnly yango-session cookie on cross-origin API calls.
axios.defaults.withCredentials = true;
// Disable request timeouts so long-running API calls are not aborted
axios.defaults.timeout = 0;

import Swal from "sweetalert2";
window.Swal = Swal;

import { errorHandler } from "./errorHandler";
window.errorHandler = errorHandler;

// See HOTFIX comment above for why this global is re-injected.
import { responses } from "./responses";
window.responses = responses;

import { dateTimeManager } from "./TimeManager";
window.$dateTimeManager = dateTimeManager;
Vue.prototype.$dateTimeManager = dateTimeManager;

import { utils } from "./utils";
window.$utils = utils;
Vue.prototype.$utils = utils;

// usageTracker (AB#4277 / FE-2 — Yango usage tracking workstream):
// Batch-queue client-side telemetry sink. Port of Libertas
// `Libertas - FrontEnd/src/services/usageTracker.js` (100 LOC,
// production-grade, 30s flush + 5min backoff cap). The tracker ships
// page-view, form-submit, and click events to SynergyApi
// `POST /api/v1/usage/record/batch`. See
// `docs/design-docs/2026-06-10-yango-usage-tracking-PLAN.md` §3 FE-1..FE-7
// and `docs/design-docs/2026-06-10-yango-usage-tracking-SPEC.md` for the
// full contract. Exposing it as `window.usageTracker` mirrors the
// `window.errorHandler` precedent so `.vue` SFCs (LogIn.vue, Otp.vue,
// InvoiceStatus.vue) can call `window.usageTracker.track(...)` without
// needing a separate import. The `app_name` is fixed to
// "yango-reports-app" (SynergyApi id 72, WEB_APP — confirmed via
// `GET /api/v1/usage/apps` 2026-06-10).
import { usageTracker } from "./usageTracker";
window.usageTracker = usageTracker;

import VueGoodTablePlugin from "vue-good-table";
// CSS moved to main.ts for proper loading order
Vue.use(VueGoodTablePlugin);

// HOTFIX 2026-06-10 (production login blocker — AB#4259 follow-up):
// The prior cleanup in AB#4244 removed `window.responses` on the
// assumption that "no TS consumer references the global". That
// assumption was wrong: LogIn.vue, Otp.vue, ResetPassword.vue, and
// RangeSelector.vue all call `responses.showSuccess` /
// `responses.showWarning` as if it were a global (the original
// `responses.js` exposed itself on `window`, and the call sites predate
// the TS conversion). errorHandler.ts has its own explicit import,
// but the .vue SFCs do not. Re-injecting the global here restores
// every call site with one line, in line with the project's
// pre-existing `window.errorHandler`, `window.usageTracker`, etc.
// pattern. Long-term: the .vue SFCs should import `responses` directly
// (PR-4 follow-up).

/**
 * Normalizes the Yango API base URL so it ends with exactly one trailing slash.
 *
 * @param raw - Raw server URL from env or config (may have zero or many trailing slashes).
 * @returns URL with a single trailing `/` suitable for path concatenation.
 */
export function normalizeYangoServerUrl(raw: string): string {
  return raw.replace(/\/+$/, "") + "/";
}

// window.yangoServer = "http://192.168.1.222:8181/yango_server/";
// Normalize the base URL so it always ends with exactly one "/", regardless of
// how the env var or fallback is set. The codebase's endpoint paths (api/*,
// Reports/*, invoice/*, showInvoice/*) do NOT include a leading slash, so the
// base URL must end with "/" to avoid producing "yangoapi" or "yango//api"
// joins. (Founder directive 2026-06-09: no double slashes, no missing slashes.)
const _yangoServerRaw = import.meta.env.VITE_YANGO_SERVER || "https://yango.abc.co.zm:8443/yango";
window.yangoServer = normalizeYangoServerUrl(_yangoServerRaw);

/**
 * Resolves the Print API base URL from env with a production fallback.
 *
 * @returns Print API base URL ending with exactly one trailing slash.
 */
export function getPrintDocUrl(): string {
  const raw =
    import.meta.env.VITE_PRINTDOC_URL ||
    "https://overflow.abc.co.zm/PrintApi/debtor/burntofi_chrilan/";
  return normalizeYangoServerUrl(raw);
}

window.printdoc = getPrintDocUrl();

// Swal.mixin's customClass field is typed SweetAlertCustomClass
// (an object), but the project uses a string "toastr" (a CSS class
// selector). This is a known sweetalert2 typing gap; the runtime
// accepts strings. Cast to any with justification.
const Toast: ReturnType<typeof Swal.mixin> = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3500,
  showCloseButton: true,
  customClass: "toastr",
  timerProgressBar: true,
  didOpen: (toast: HTMLElement): void => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
} as any);

window.pageOptions = {
  enabled: true,
  mode: "pages",
  perPage: 10,
  position: "bottom",
  perPageDropdown: [10, 100, 200],
  dropdownAllowAll: false,
  jumpFirstOrLast: true,
  pageLabel: "page", // for 'pages' mode
};
window.Toast = Toast;

// HOTFIX 2026-06-10: `X-Request-Id` was removed from the usageTracker
// flush POST (CORS preflight compatibility — see
// `usageTracker.ts:flush()` JSDoc). The previous consumer of
// `window.generateUUID` on the usageTracker call site is gone, but
// `CustomModal.vue:43` still calls `generateUUID()` to build a unique
// modal id, so the helper is preserved. The signature remains
// `(prefix?: string) => string` (the prefix is currently ignored by
// this implementation but is kept for future use).
/**
 * Generates a RFC-4122-style UUID v4 string (36 chars including hyphens).
 *
 * @returns New UUID string suitable for DOM ids and correlation hooks.
 */
export function generateUUID(): string {
  let uuid = "";
  let i: number;
  let random: number;
  for (i = 0; i < 32; i++) {
    random = (Math.random() * 16) | 0;
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      uuid += "-";
    }
    uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
  }
  return uuid;
}

window.generateUUID = generateUUID;

// AD-Q17: attach legacy session-id header when tab-scoped fallback is set
// (cross-origin dev/proxy cannot send SameSite=Strict httpOnly cookie).
axios.interceptors.request.use((config) => {
  const sessionId = getSessionHeaderId();
  if (!sessionId) {
    return config;
  }
  const url = config.url ?? '';
  if (isPublicAuthUrl(url) || isUsageTrackingUrl(url)) {
    return config;
  }
  config.headers = config.headers ?? {};
  config.headers['session-id'] = sessionId;
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSessionExpiredError(error)) {
      (error as Record<string, unknown>)[SESSION_EXPIRED_HANDLED] = true;
      handleSessionExpiredResponse();
    }
    return Promise.reject(error);
  },
);
